import { requiredDurableStore } from "../lib/clientStorage";
import { supabase } from "../lib/supabase";
import { ApiError } from "../lib/api";

interface SmsIntent { id: string; fingerprint: string }
export function readSmsIntent(key: string): SmsIntent | null {
  const raw = requiredDurableStore.getItem(key);
  if (!raw) return null;
  const value = JSON.parse(raw);
  if (typeof value.id !== 'string' || typeof value.fingerprint !== 'string') {
    throw new Error('The saved send record cannot be read. Check the thread before sending.');
  }
  return value;
}

export async function withSmsSendIntent<T>(subject: string, payload: unknown, send: (id: string) => Promise<T>, uncertain: (result: T) => boolean): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user.id) throw new Error('Sign in before sending.');
  const key = `sms_send:${session.user.id}:${subject}`;
  if (!navigator.locks) throw new Error('This browser cannot safely coordinate sends. Use a supported browser.');
  return navigator.locks.request(key, async () => {
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(payload)));
    const fingerprint = Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
    const current = await supabase.auth.getSession();
    if (current.data.session?.user.id !== session.user.id) throw new Error('Your account changed. Reopen the conversation before sending.');
    const prior = readSmsIntent(key);
    if (prior && prior.fingerprint !== fingerprint) throw new Error('A previous send needs checking. Open or refresh the thread before sending different text.');
    const intent = prior ?? { id: crypto.randomUUID(), fingerprint };
    // Fail closed if durable storage fails. Store only an id/hash, never renter text.
    requiredDurableStore.setItem(key, JSON.stringify(intent));
    if (readSmsIntent(key)?.id !== intent.id) throw new Error('Could not save the send record. Nothing was sent.');
    try {
      const result = await send(intent.id);
      if (!uncertain(result)) requiredDurableStore.removeItem(key);
      return result;
    } catch (error) {
      if (error instanceof ApiError && [400, 403, 404, 422].includes(error.status)) requiredDurableStore.removeItem(key);
      throw error;
    }
  });
}
